import React from 'react';
import { ChartSchema } from '../../../types/types';

interface LineChartRendererProps {
  series: ChartSchema['series'];
  xScale: (value: string | number) => number;
  yScale: (value: number) => number;
  yMinMax?: { min: number; max: number };
  chartType?: string; // Add chart type to determine whether to show area fills
}

/**
 * Generate path for a series
 */
const generateLinePath = (
  series: ChartSchema['series'][0],
  xScale: (value: string | number) => number,
  yScale: (value: number) => number
): string => {
  return series.data
    .map((point, index) => {
      const x = xScale(point.x);
      const y = yScale(point.y);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
};

/**
 * Generate area path for a series
 */
const generateAreaPath = (
  series: ChartSchema['series'][0],
  xScale: (value: string | number) => number,
  yScale: (value: number) => number,
  yMinMax?: { min: number; max: number }
): string => {
  // Use 0 as the baseline for financial charts, or the minimum y value if specified
  const baseY = yScale(yMinMax?.min || 0);

  let path = series.data
    .map((point, index) => {
      const x = xScale(point.x);
      const y = yScale(point.y);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Complete the path by going back to the baseline
  const lastPoint = series.data[series.data.length - 1];
  const firstPoint = series.data[0];

  path += ` L ${xScale(lastPoint.x)} ${baseY} L ${xScale(firstPoint.x)} ${baseY} Z`;

  return path;
};

/**
 * Line Chart Renderer Component
 */
export const LineChartRenderer: React.FC<LineChartRendererProps> = ({
  series,
  xScale,
  yScale,
  yMinMax,
  chartType = 'line',
}) => {
  return (
    <>
      {/* Area fills for each series - only show for area chart type */}
      {chartType === 'area' &&
        series.map(series => (
          <path
            key={`area-${series.id}`}
            d={generateAreaPath(series, xScale, yScale, yMinMax)}
            fill={series.color}
            fillOpacity={series.fillOpacity || 0.2}
            stroke="none"
          />
        ))}

      {/* Lines for each series */}
      {series.map(series => (
        <path
          key={`line-${series.id}`}
          d={generateLinePath(series, xScale, yScale)}
          fill="none"
          stroke={series.color}
          strokeWidth={2}
        />
      ))}

      {/* Data points */}
      {series.map(series =>
        series.data.map((point, i) => (
          <circle
            key={`point-${series.id}-${i}`}
            cx={xScale(point.x)}
            cy={yScale(point.y)}
            r={4}
            fill="white"
            stroke={series.color}
            strokeWidth={2}
          />
        ))
      )}
    </>
  );
};

export default LineChartRenderer;
